/**
 * PATH: packages/ui/src/kanban/errorCopy.ts
 * FILE_ID: UI-KANBAN-ERRORCOPY-001
 * PURPOSE: One pure mapping from a caught kanban error to operator-facing
 *          copy: classify (stale / network / auth / server / client /
 *          unknown) and humanize. Raw transport strings are NEVER returned.
 *          Dynamic-import 404s are stale, not network.
 * WHY: ARC-8 U10 / BUG-2026-07-02-KANBAN-BOARD-ERROR-GATE — the operator's
 *      "server not connected" complaint: raw fetch-layer error text leaked
 *      into full-screen error states. Every error surface must speak the
 *      same plain language and distinguish "can't reach the server" from a
 *      real 4xx/5xx rejection (spec §4.2: offline/network distinguishable).
 * FLOW:
 *   ┌───────────────┐    ┌──────────────────────┐    ┌───────────────────┐
 *   │ unknown error │ →  │ classifyKanbanError  │ →  │ { kind, title,    │
 *   │ (query/mut.)  │    │ + humanizeKanbanError│    │   message }       │
 *   └───────────────┘    └──────────────────────┘    └───────────────────┘
 * DEPENDENCIES:
 *   - ./api (KanbanNetworkError): the typed never-reached-server error.
 * RELATED:
 *   - components/KanbanBoardView.tsx: board-level gate (primary consumer)
 *   - components/board-view/BoardStatusRibbons.tsx: stale-board ribbon copy
 */
import { looksLikeStaleBundle } from '../components/staleBundle'
import { isKanbanNetworkError } from './api'

export type KanbanErrorKind =
  | 'stale'
  | 'network'
  | 'auth'
  | 'server'
  | 'client'
  | 'unknown'

export interface KanbanErrorCopy {
  kind: KanbanErrorKind
  title: string
  /** Plain-words body. Never a raw transport string. */
  message: string
}

/** Transport-level strings the browser/axios emit for a request that never
 *  reached the server. These must never be shown to an operator. */
const RAW_NETWORK_MESSAGES = [
  'failed to fetch', // Chromium fetch
  'network error', // axios / Firefox fetch ("NetworkError when attempting…")
  'load failed', // Safari fetch
  'networkerror',
]

const HTTP_STATUS_RE = /\b(4\d\d|5\d\d)\b/

/** f(error) -> coarse class. Network detection covers the typed
 *  KanbanNetworkError (status 0), raw fetch TypeErrors, and the offline
 *  navigator state; auth = 401/403 text markers from the fetch layer. */
export function classifyKanbanError(error: unknown): KanbanErrorKind {
  if (looksLikeStaleBundle(error)) return 'stale'
  if (isKanbanNetworkError(error)) return 'network'
  const msg =
    error instanceof Error ? error.message.toLowerCase() : String(error ?? '').toLowerCase()
  if (RAW_NETWORK_MESSAGES.some((raw) => msg.includes(raw))) return 'network'
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'network'
  if (msg.includes('401') || msg.includes('403') || msg.includes('unauthorized') || msg.includes('forbidden')) {
    return 'auth'
  }
  const status = msg.match(HTTP_STATUS_RE)?.[1]
  if (status) return status.startsWith('5') ? 'server' : 'client'
  return 'unknown'
}

/** True when a message is safe to show verbatim (structured copy from our
 *  own fetch layer / DRF detail), false for raw transport noise. */
function isPresentableMessage(msg: string): boolean {
  const lower = msg.toLowerCase()
  if (!msg.trim()) return false
  if (RAW_NETWORK_MESSAGES.some((raw) => lower.includes(raw))) return false
  if (lower.startsWith('typeerror') || lower.startsWith('error:')) return false
  return true
}

/**
 * f(error) -> operator copy. The network class gets fixed human copy with a
 * retry framing; HTTP classes keep the fetch layer's structured detail when
 * presentable and fall back to plain copy when not.
 */
export function humanizeKanbanError(error: unknown): KanbanErrorCopy {
  const kind = classifyKanbanError(error)
  const rawMsg = error instanceof Error ? error.message : ''

  switch (kind) {
    case 'stale':
      return {
        kind,
        title: 'This page is out of date',
        message:
          'A new version just deployed. Reload to pick it up. Nothing was lost.',
      }
    case 'network':
      return {
        kind,
        title: "Can't reach the server",
        message:
          'Your connection dropped or the server is unreachable. Nothing was lost — retry when you\u2019re back online.',
      }
    case 'auth':
      return {
        kind,
        title: 'Session problem',
        message:
          'Your session expired or you don\u2019t have access to this board. Reconnect or sign in again.',
      }
    case 'server':
      return {
        kind,
        title: 'The server hit an error',
        message: isPresentableMessage(rawMsg)
          ? rawMsg
          : 'Something failed on our side. Retry in a moment — the team is notified of server errors.',
      }
    case 'client':
      return {
        kind,
        title: "Couldn't load",
        message: isPresentableMessage(rawMsg)
          ? rawMsg
          : 'The request was rejected. Refresh the page; if it persists, tell support what you were doing.',
      }
    default:
      return {
        kind,
        title: "Couldn't load",
        message: isPresentableMessage(rawMsg)
          ? rawMsg
          : 'Something unexpected went wrong. Retry — if it keeps happening, tell support what you were doing.',
      }
  }
}
